import { LOGIN_RESULT } from './brokerCheck';

// Copy for the EA Connect console surfaces.
export const feedCopy = {
  live: 'live',
  idle: 'idle',
  paused: 'paused',
  errors: 'ERRORS',
  pause: 'PAUSE',
  resume: 'RESUME',
  // "RESUME · 12 new"; capped when more lines arrived than the tail holds.
  resumeWithNew: (count: number, capped: boolean) => `RESUME · ${count}${capped ? '+' : ''} new`,
  pauseHint: 'Freeze this view — the bot keeps running',
  resumeHint: 'Jump back to the newest lines',
  zoneHint: (zone: string) => `Times shown in your time zone (${zone})`
};

export const inputsCopy = {
  pagerLabel: 'Input sections',
  ungrouped: 'General',
  page: (n: number) => `Page ${n}`,
  // "Risk · 2/3" when one section is split across pages.
  groupPage: (group: string, part: number, parts: number) => (parts > 1 ? `${group} · ${part}/${parts}` : group),
  changedHint: (n: number) => `${n} changed on this page`
};

const emptyValue = '(empty)';

export const settingsCopy = {
  // Under an input's label: its MQL name when a description is the label, and the default once the value moves off it.
  caption: (name: string, defaultValue?: string) =>
    [name, defaultValue === undefined ? '' : `default ${defaultValue || emptyValue}`].filter(Boolean).join(' · '),
  changesTitle: (n: number) => `${n} input${n === 1 ? '' : 's'} change`,
  change: (from: string, to: string) => `${from || emptyValue} → ${to || emptyValue}`
};

export const symbolCopy = {
  label: 'Symbol',
  noMatches: 'No matching symbol on this account'
};

// Deploy wizard labels: the step rail (in stage order) and the Broker and Configure cards.
export const deployCopy = {
  railSteps: ['Upload EA', 'Extract inputs', 'Broker account', 'Configure', 'Launch'],
  brokerTitle: 'Broker account',
  brokerCaption: 'The account your instance trades on',
  newProfile: 'New profile',
  botName: 'Bot name'
};

// The bot page's Auto-restart row.
export const restartCopy = {
  off: 'Off',
  on: (used: number, max: number) => `On · ${used}/${max} used · 24h`,
  left: (n: number) => `${n} left in this window`,
  spent: 'Budget spent: no auto-recovery until the window rolls'
};

// The cancel panel at the bottom of the Fleet.
export const planCopy = {
  cancelDescription:
    "EA Hosting stays on until the end of the period you've paid for. Then your bots stop. They and their settings aren't deleted: subscribe again and start them from the Fleet.",
  cancelAction: 'Cancel hosting'
};

export const brokerCheckCopy = {
  // Short status per ValidateBrokerLogin result, for profile cards; unknown results read as `unverified`.
  results: {
    [LOGIN_RESULT.checking]: 'Checking…',
    [LOGIN_RESULT.ok]: 'Connected',
    [LOGIN_RESULT.serverUnresolvable]: 'Server not found',
    [LOGIN_RESULT.loginFailed]: 'Wrong login or password',
    [LOGIN_RESULT.loginUnconfirmed]: 'Login not confirmed',
    [LOGIN_RESULT.tradingDisabled]: "Can't trade",
    [LOGIN_RESULT.noConnection]: 'No connection',
    [LOGIN_RESULT.unavailable]: 'Not verified'
  } as Record<string, string>,
  unverified: 'Not verified',
  // Longer lines for the selected profile; the server's own message wins when it sends one.
  checkingDetail: 'Checking the login with your broker…',
  okDetail: 'Connected. Login and trading confirmed.',
  blockedDetail: 'Fix this broker account to continue.',
  warnDetail: "We couldn't verify this login right now. You can still launch.",
  serverNotFound: (platform: string) => `We couldn't find this server. Enter its name exactly as shown in your ${platform} login window.`,
  saveButton: 'Save & check',
  savingButton: 'Saving…',
  saveHint: 'Saves this profile, then checks the login with your broker. It takes a few seconds.',
  brokerName: 'Broker name',
  brokerNamePlaceholder: 'e.g. IC Markets',
  server: 'Server',
  serverPlaceholder: 'e.g. FBS-Demo',
  accountNumber: 'Account number',
  password: 'Password',
  demo: 'Demo',
  live: 'Live',
  demoAccount: 'Demo account',
  liveAccount: 'Live account',
  defaultProfileName: (login: string | number) => `Broker ${login}`,
  name: 'Name',
  edit: 'Edit',
  delete: 'Delete',
  cancel: 'Cancel',
  save: 'Save',
  editTitle: 'Edit broker profile',
  passwordKeepHint: 'Leave blank to keep the current password.',
  accountLocked: (bots: string[]) =>
    `Used by ${bots.join(', ')}, so only the name and password can change. To change the broker account, delete those bots first.`,
  accountInUse: "Broker, server, account number and demo/live can't change while an EA uses this profile.",
  profileSaved: 'Broker profile saved',
  restartForPassword: 'Restart your EAs to use the new password.',
  deleteTitle: 'Delete broker profile?',
  deleteBody: (label: string) => `"${label}" and its saved password will be removed.`,
  deleted: 'Broker profile deleted',
  inUseBy: (bots: string[]) => `Used by ${bots.join(', ')}`,
  notInUse: 'Not used by any bot',
  accountsTitle: 'Broker accounts',
  accountsCaption: 'Saved logins your bots trade on. Change a password, or delete an account no bot uses.',
  deleteInUse: (bots: string[]) => `Used by ${bots.join(', ')}. Delete those bots first.`,
  deleteFailed: 'Could not delete the broker profile',
  checkAgain: 'Check again',
  checkAgainIn: (clock: string) => `Check again in ${clock}`,
  saveToLaunch: 'Save & check this profile to enable Launch.',
  configureLocked: 'Pick a connected broker account above to configure your bot.',
  pickProfile: 'Pick or create a broker profile to launch.',
  launchBlocked: 'Fix the broker account to launch.',
  saveFailed: 'Could not save the broker profile'
};
